<template>
  <div class="store-picker">
    <!-- 열 때만 살짝 떠오르고, 닫을 때는 바로 사라진다 (화면 전환이 눈에 거슬리지 않게) -->
    <transition name="store-picker">
      <store-picker-layer
          v-if="open"
          :initial-store-code="value ? value.strCode : ''"
          @select="onSelect"
          @close="close"
      />
    </transition>
  </div>
</template>

<script>
import StorePickerLayer from "@/components/SUPPORT/StorePickerLayer.vue";
import { isStoreRowList } from "@/utils/storeFinder";
import { lockPageScroll, unlockPageScroll } from "@/utils/scrollLock";

// 신청서의 "상담받을 매장" 고르기 (v-model = 고른 매장, :open.sync = 고르는 화면이 열렸는지)
// 매장 칸 모양은 각 신청 페이지에 있고, 여기서는 전체 화면 매장찾기를 열고 닫는 일만 맡는다
export default {
  name: "StorePicker",
  components: { StorePickerLayer },
  props: {
    value: { type: Object, default: null },
    open: { type: Boolean, default: false },
  },
  computed: {
    storeRows() {
      const rows = this.$store.state.support.storeData;
      return isStoreRowList(rows) ? rows : [];
    },
  },
  watch: {
    // 열려 있는 동안 뒤 페이지 스크롤을 막고, 뒤로가기로 닫히게 방문 기록 한 칸 (주소는 그대로)
    open(isOpen) {
      if (isOpen) {
        lockPageScroll();
        history.pushState({ ...history.state, storePicker: 1 }, "");
      } else {
        unlockPageScroll();
      }
    },
  },
  async mounted() {
    window.addEventListener("popstate", this.onPopState);
    // 바깥(떠 있는 "간편 상담신청" 버튼 등)에서 닫아달라고 할 때
    window.addEventListener("store-picker:close", this.onExternalClose);
    this.dropStaleLayer();
    if (!this.storeRows.length) await this.$store.dispatch("support/FETCH_STORE");
    this.applyDefault();
  },
  beforeDestroy() {
    window.removeEventListener("popstate", this.onPopState);
    window.removeEventListener("store-picker:close", this.onExternalClose);
    if (this.open) unlockPageScroll();
  },
  methods: {
    // 같은 페이지에 머무를 때(keepHistory 없음)는 쌓인 기록까지 정리하고,
    // 다른 페이지로 이동하면서 닫을 때는 화면만 닫는다 (기록은 돌아올 때 정리됨)
    onExternalClose(e) {
      if (!this.open) return;
      // 바로 닫고, 쌓아둔 방문 기록은 뒤에서 조용히 걷어낸다
      this.$emit("update:open", false);
      const state = history.state || {};
      const navigating = !!(e && e.detail && e.detail.navigating);
      if (!navigating && state.storePicker) history.go(-(1 + (state.findStoreLayers || 0)));
    },

    onSelect(store) {
      this.$emit("input", store);
      this.close();
    },

    // 전체 화면 + 그 안에서 연 시트·상세 기록까지 한 번에 걷어낸다 (→ onPopState에서 닫힘)
    close() {
      const state = history.state || {};
      if (this.open && state.storePicker) {
        history.go(-(1 + (state.findStoreLayers || 0)));
        return;
      }
      this.$emit("update:open", false);
    },

    onPopState() {
      if (this.open && !(history.state && history.state.storePicker)) {
        this.$emit("update:open", false);
      }
    },

    // 매장 고르는 화면이 열린 채 새로고침·다른 페이지에서 돌아오면 그때 쌓인 기록을 걷어낸다
    dropStaleLayer() {
      const state = history.state || {};
      if (state.storePicker) history.go(-(1 + (state.findStoreLayers || 0)));
    },

    // 기본값: 매장찾기 [상담예약]으로 왔을 때(?store=)만 그 매장을 채운다
    // 위치를 허용했더라도 가까운 매장을 대신 고르지 않는다 (고객이 직접 고르도록)
    applyDefault() {
      if (this.value) return;
      const code = this.$route.query.store;
      const linked = code && this.storeRows.find((s) => s.strCode === String(code));
      if (linked) this.$emit("input", linked);
    },
  },
};
</script>

<style lang="scss" scoped>
// 열 때만 살짝 떠오르듯이 (닫을 때는 애니메이션 없이 바로 사라진다)
.store-picker-enter-active {
  transition: opacity 0.18s ease-out, transform 0.18s ease-out;
}
.store-picker-enter {
  opacity: 0;
  transform: translateY(12px);
}
@media (prefers-reduced-motion: reduce) {
  .store-picker-enter-active {
    transition: none;
  }
}
</style>
